import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { chargeInvoice, processPayment } from '../redux/feature/invoice/slice';
import { Input, Button } from 'antd';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Swal from 'sweetalert2';

const AddCard = ({
  invoiceId,
  ConnAccId,
  tripId,
  customerId,
  isExistingCustomer = false,
  companyGateway,
  remainingAmount
}) => {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [cardError, setCardError] = useState('');
  const [isCardComplete, setIsCardComplete] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      setAmount(remainingAmount?.toString() || '');
    }
  }, [invoiceId, remainingAmount]);

  const validateForm = () => {
    let isValid = true;

    const paymentAmount = parseFloat(amount);
    if (!amount || isNaN(paymentAmount)) {
      setAmountError('Please enter a valid amount');
      isValid = false;
    } else if (paymentAmount < 1) {
      setAmountError('Amount must be at least 1');
      isValid = false;
    } else if (paymentAmount > remainingAmount) {
      if (invoiceId !== undefined) {
        setAmountError('Amount must be less than or equal to Total-Due Amount');
      } else {
        setAmountError('Amount must be less than or equal to Remaining Amount');
      }
      isValid = false;
    } else {
      setAmountError('');
    }

    if (!isCardComplete) {
      setCardError('Please complete your card details');
      isValid = false;
    } else {
      setCardError('');
    }

    return isValid;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }

    const paymentAmount = parseFloat(amount);
    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);

    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(error.message);
        throw new Error(error.message);
      }

      const paymentData = {
        paymentMethodId: paymentMethod.id,
        amount: paymentAmount,
        ...(customerId && { customerId }),
        ...(isExistingCustomer && { isExistingCustomer: true }),
        ...(!isExistingCustomer && { isOneTimePayment: true })
      };

      if (invoiceId) {
        paymentData.invoiceId = invoiceId;
        const result = await dispatch(chargeInvoice(paymentData));

        if (result.payload?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Payment Successful',
            text: 'Your invoice payment has been processed successfully.',
            confirmButtonColor: "#058CBD",
          }).then(() => {
            window.location.reload();
          });
        } else if (result.error) {
          throw new Error(result.payload?.message || 'Payment failed');
        }
      } else {
        paymentData.tripId = tripId;
        const result = await dispatch(processPayment(paymentData));

        if (result.payload?.tripUpdated) {
          Swal.fire({
            icon: 'success',
            title: 'Payment Successful',
            text: 'Your payment has been processed successfully.',
            confirmButtonColor: "#058CBD",
          }).then(() => {
            window.location.reload();
          });
        } else if (result.error) {
          throw new Error(result.payload?.message || 'Payment failed');
        }
      }

    } catch (error) {
      console.error('Error processing payment:', error);
      Swal.fire({
        icon: 'error',
        title: 'Payment Failed',
        text: error.message || 'Unable to process payment. Please try again.',
        confirmButtonColor: "#058CBD",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value > remainingAmount) {
      if (invoiceId !== undefined) {
        setAmountError('Amount must be less than or equal to Total-Due Amount');
      } else {
        setAmountError('Amount must be less than or equal to Remaining Amount');
      }
      return;
    }
    setAmount(value);

    if (amountError) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 1) {
        setAmountError('');
      }
    }
  };

  const handleCardChange = (event) => {
    if (event.error) {
      setCardError(event.error.message);
      setIsCardComplete(false);
    } else {
      setCardError('');
      setIsCardComplete(event.complete);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">
          <span>Amount <span className="text-red-500">*</span></span>
        </label>
        <Input
          type="number"
          value={amount}
          onChange={handleAmountChange}
          placeholder="Enter amount to pay"
          className="rounded-md h-[44px]"
          min={1}
          step="0.01"
          status={amountError ? 'error' : ''}
          disabled={!!invoiceId}
        />
        {amountError && (
          <div className="text-red-500 text-sm mt-1">{amountError}</div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          <span>Card Details <span className="text-red-500">*</span></span>
        </label>
        <div className={`border rounded-md p-3 ${cardError ? 'border-red-500' : ''}`}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: true,
            }}
            onChange={handleCardChange}
          />
        </div>
        {cardError && (
          <div className="text-red-500 text-sm mt-1">{cardError}</div>
        )}
      </div>

      <Button
        type="primary"
        onClick={handlePayment}
        loading={isProcessing}
        disabled={!stripe || !elements || !amount || !isCardComplete}
        className="bg-[#058CBD] hover:bg-[#01A7DC] text-white font-bold py-4 px-4 rounded w-full"
      >
        {isProcessing ? 'Processing...' : (isExistingCustomer ? 'Pay with New Card' : 'Pay Now')}
      </Button>

      {companyGateway && (
        <div className="text-sm text-gray-500 text-center">
          Processing via {companyGateway}
        </div>
      )}
    </div>
  );
};


export default AddCard;
